import { useMemo, useState, type ReactNode } from 'react';
import { closePrice, D, fmt, fmtDateTime, fmtPct, fmtPrice, fmtUsd, fmtUsdSigned, freeMargin, orderTypeLabel, pairLabel, pnl, usdPerPoint, type CloseReason, type Order, type Position } from '@platform/shared';
import { api, ApiError } from '../lib/api';
import { useStore } from '../lib/store';
import { marketStore, useInstrument, useTicker } from '../stores/market';
import { useActiveAccount, useMe } from '../stores/session';
import { useInvestmentAccounts, useInvestmentTotals } from '../stores/investments';
import { reloadManaged, useManagedAccounts, useManagedClosed, useManagedPositions, useManagedTotals } from '../stores/managed';
import { reloadTrading, useClosedPositions, useOrders, usePositions } from '../stores/trading';
import { AllocateFundsDialog } from './AllocateFunds';
import { HelpTip } from './HelpTip';
import { InstrumentIcon } from './InstrumentIcon';
import { MarginWarning } from './TradeNotices';
import { closedCosts, closedTotals, DEFAULT_PERIOD, inPeriod, periodLabel, PeriodSelect, type Period } from './PeriodPicker';
import { cx, Empty, fmtLots, Icon, Modal, sideLabel, Spinner, TradeId, unitLabel } from './ui';
import { DESKTOP_QUERY, useMedia } from '../lib/media';
import { NumInput } from './NumInput';

const time = (iso: string) => fmtDateTime(iso, { year: false });
const DEC_RE = /^\d*\.?\d*$/;
const signCls = (x: D | null) => (!x ? 'text-muted' : x.isNeg() ? 'text-down' : 'text-up');

export function useAction() {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Request failed');
    } finally {
      setBusy(null);
    }
  };
  return { err, busy, run };
}

export type Run = ReturnType<typeof useAction>['run'];

/** Mobile card header: the asset with its trade number beside it. */
function CardAsset({ tradeId, ...asset }: { tradeId: number | null; symbol: string; long: boolean; lev?: number }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <AssetCell {...asset} />
      {tradeId != null && <TradeId id={tradeId} />}
    </span>
  );
}

function AssetCell({ symbol, long, lev }: { symbol: string; long: boolean; lev?: number }) {
  return (
    <span className="flex items-center gap-2 font-sans">
      <span className={cx('h-[18px] w-[3px] rounded-sm', long ? 'bg-up' : 'bg-down')} />
      <InstrumentIcon symbol={symbol} size={20} />
      <span className="font-semibold">{pairLabel(symbol)}</span>
      <span className={cx('rounded px-1.5 py-px text-[10.5px] font-semibold', long ? 'bg-up-bg text-up' : 'bg-down-bg text-down')}>
        {sideLabel(long ? 'long' : 'short')}
        {lev ? ` ${lev}x` : ''}
      </span>
    </span>
  );
}

const btn = 'h-[26px] rounded-md border border-line-strong px-3 font-sans text-xs text-fg disabled:opacity-50';
const head = 'sticky top-0 z-[1] border-b border-line bg-bg px-4 py-2 text-[11px] text-muted';
const row = 'num items-center whitespace-nowrap border-b border-line px-4 py-2 text-xs hover:bg-panel';

// ---- TP / SL editor ----
export function TpSlModal({ p, onClose, run, busy, err }: { p: Position; onClose: () => void; run: Run; busy: boolean; err: string | null }) {
  const inst = useInstrument(p.symbol);
  const dp = inst?.priceDecimals ?? 2;
  const ccy = inst?.priceCcy ?? 'USD';
  const [tp, setTp] = useState(p.tpPrice ? D.from(p.tpPrice).toFixed(dp) : '');
  const [sl, setSl] = useState(p.slPrice ? D.from(p.slPrice).toFixed(dp) : '');
  const save = () =>
    void run(`tpsl:${p.id}`, async () => {
      await api.patch(`/api/positions/${p.id}`, { tpPrice: tp || null, slPrice: sl || null });
      onClose();
    });
  const input = 'num h-10 w-full rounded-lg border border-line bg-bg px-3 outline-none focus:border-line-strong placeholder:text-muted';
  return (
    <Modal open onClose={onClose} title={`TP / SL · ${pairLabel(p.symbol)} ${sideLabel(p.side)} ${p.leverage}x`}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <p className="m-0 text-xs text-muted">
          Entry <span className="num text-fg">{fmtPrice(p.entryPrice, dp, ccy)}</span>. Leave a field empty to remove it.
        </p>
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Take profit price
          <NumInput value={tp} onValue={(v) => DEC_RE.test(v) && setTp(v)} placeholder="None" className={cx(input, 'text-up')} />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Stop loss price
          <NumInput value={sl} onValue={(v) => DEC_RE.test(v) && setSl(v)} placeholder="None" className={cx(input, 'text-down')} />
        </label>
        {err && <p className="m-0 text-xs text-down" role="alert">{err}</p>}
        <div className="grid grid-cols-2 gap-2.5">
          <button type="button" onClick={onClose} className="h-[42px] rounded-lg border border-line-strong font-semibold text-fg">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="h-[42px] rounded-lg bg-accent font-semibold text-white disabled:opacity-50">
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---- Open positions ----
const POS_COLS = 'grid grid-cols-[0.7fr_1.3fr_2.7fr_0.7fr_1fr_1fr_1fr_0.9fr_1.4fr_1.4fr_1fr] gap-2';

/** Live mark, PnL and ROE for one open position. */
export function useLivePosition(p: Position) {
  const inst = useInstrument(p.symbol);
  const t = useTicker(p.symbol);
  const cs = usdPerPoint(inst?.contractSize ?? '1', t?.fx);
  const exit = t ? closePrice(p.side, { bid: D.from(t.bid), ask: D.from(t.ask) }) : null;
  const upnl = exit ? pnl(p.side, p.entryPrice, exit, p.qty, cs).add(p.swap) : null;
  const roe = upnl && D.from(p.margin).isPos() ? upnl.div(p.margin).mul(100) : null;
  const value = t ? D.from(p.qty).mul(t.mark).mul(cs) : null;
  return { inst, t, dp: inst?.priceDecimals ?? 2, ccy: inst?.priceCcy ?? 'USD', upnl, roe, value, long: p.side === 'long' };
}

export function closePos(run: Run, p: Position) {
  return run(`close:${p.id}`, () => api.post(`/api/positions/${p.id}/close`));
}

function PositionRow({ p, run, busy, onEdit }: { p: Position; run: Run; busy: string | null; onEdit: () => void }) {
  const { inst, t, dp, ccy, upnl, roe, value, long } = useLivePosition(p);
  return (
    <>
      <div className={cx(POS_COLS, row)}>
        <TradeId id={p.tradeId} />
        <span className="text-muted">{time(p.openedAt)}</span>
        <span className="flex min-w-0 items-center gap-2">
          <AssetCell symbol={p.symbol} long={long} lev={p.leverage} />
          {p.managedBy && <ShortManagedTag name={p.managedBy.name} />}
        </span>
        <span className={long ? 'text-up' : 'text-down'} title={`${fmt(p.qty, inst?.qtyDecimals ?? 4)} ${unitLabel(inst?.unit ?? '', p.qty)}`}>
          {long ? '+' : '-'}
          {fmtLots(p.qty, inst?.lotSize ?? '1')}
        </span>
        <span>{value ? fmtUsd(value) : '—'}</span>
        <span>{fmtPrice(p.entryPrice, dp, ccy)}</span>
        <span>{t ? fmtPrice(t.mark, dp, ccy) : '—'}</span>
        <span>{fmtUsd(p.marginHeld ?? p.margin)}</span>
        <span className={signCls(upnl)} title={`Swap ${fmtUsdSigned(p.swap)} · Open fee ${fmtUsd(p.openFee)} (charged at open)`}>
          {upnl ? fmtUsdSigned(upnl) : '—'} {roe && <span className="opacity-80">({fmtPct(roe, 1)})</span>}
        </span>
        <span>
          <span className="text-up">{p.tpPrice ? fmtPrice(p.tpPrice, dp, ccy) : '—'}</span>
          <span className="text-muted"> / </span>
          <span className="text-down">{p.slPrice ? fmtPrice(p.slPrice, dp, ccy) : '—'}</span>
        </span>
        {p.managedBy ? (
          <span className="flex min-w-0 justify-end">
            <ManagedTag p={p} />
          </span>
        ) : (
          <span className="flex justify-end gap-1.5">
            <button type="button" onClick={onEdit} aria-label={`Edit TP/SL for ${p.symbol}`} title="Edit TP / SL" className="grid size-[26px] place-items-center rounded-md border border-line-strong text-muted hover:text-fg">
              <Icon name="edit" size={13} />
            </button>
            <button type="button" disabled={busy === `close:${p.id}`} onClick={() => void closePos(run, p)} className={btn}>
              Close
            </button>
          </span>
        )}
      </div>
    </>
  );
}

const card = 'flex flex-col gap-2 border-b border-line px-4 py-3';
const cardBtn = 'h-9 flex-1 rounded-lg border border-line-strong font-sans text-[12.5px] font-medium text-fg disabled:opacity-50';

/** Label over value, for the mobile cards. */
function Field({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <span className={cx('flex min-w-0 flex-col', className)}>
      <span className="font-sans text-[11px] text-muted">{label}</span>
      {children}
    </span>
  );
}

function PositionCard({ p, run, busy, onEdit }: { p: Position; run: Run; busy: string | null; onEdit: () => void }) {
  const { inst, t, dp, ccy, upnl, roe, long } = useLivePosition(p);
  return (
    <div className={card}>
      <div className="flex items-center justify-between gap-2">
        <CardAsset tradeId={p.tradeId} symbol={p.symbol} long={long} lev={p.leverage} />
        <span className={cx('num text-right font-semibold', signCls(upnl))}>
          {upnl ? fmtUsdSigned(upnl) : '—'} {roe && <span className="text-[11px] font-normal opacity-80">({fmtPct(roe, 1)})</span>}
        </span>
      </div>
      <div className="num grid grid-cols-3 gap-2 text-xs">
        <Field label="Lots">
          <span className={long ? 'text-up' : 'text-down'}>
            {long ? '+' : '-'}
            {fmtLots(p.qty, inst?.lotSize ?? '1')}
          </span>
        </Field>
        <Field label="Entry">{fmtPrice(p.entryPrice, dp, ccy)}</Field>
        <Field label="Mark">{t ? fmtPrice(t.mark, dp, ccy) : '—'}</Field>
        <Field label="Margin">{fmtUsd(p.marginHeld ?? p.margin)}</Field>
        <Field label="TP / SL">
          <span className="truncate">
            <span className="text-up">{p.tpPrice ? fmtPrice(p.tpPrice, dp, ccy) : '—'}</span>
            <span className="text-muted"> / </span>
            <span className="text-down">{p.slPrice ? fmtPrice(p.slPrice, dp, ccy) : '—'}</span>
          </span>
        </Field>
      </div>
      <div className="num text-[11px] text-muted">
        Opened {time(p.openedAt)} · open fee {fmtUsd(p.openFee)}
      </div>
      {p.managedBy ? (
        <div>
          <ManagedTag p={p} />
        </div>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={onEdit} className={cardBtn}>
            Edit TP / SL
          </button>
          <button type="button" disabled={busy === `close:${p.id}`} onClick={() => void closePos(run, p)} className={cardBtn}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}

export function PositionsTable({ rows, run, busy, compact }: { rows: Position[]; run: Run; busy: string | null; compact: boolean }) {
  const [editing, setEditing] = useState<string | null>(null);
  const modal = useAction();
  const editPos = rows.find((p) => p.id === editing);
  const editor = editPos && <TpSlModal key={editPos.id} p={editPos} onClose={() => setEditing(null)} run={modal.run} busy={modal.busy !== null} err={modal.err} />;
  if (compact) {
    return (
      <div>
        {rows.length > 0 && <MarginWarning className="mx-4 my-2" />}
        {rows.map((p) => (
          <PositionCard key={p.id} p={p} run={run} busy={busy} onEdit={() => setEditing(p.id)} />
        ))}
        {!rows.length && <Empty>No open positions</Empty>}
        {editor}
      </div>
    );
  }
  return (
    <div className="min-w-[1340px]">
      <div className={cx(POS_COLS, head)}>
        <span>Order ID</span>
        <span>Opened</span>
        <span>Asset</span>
        <span>Lots</span>
        <span>Value</span>
        <span>Entry Price</span>
        <span>Mark Price</span>
        <span>Margin</span>
        <span>PnL (ROE)</span>
        <span>TP / SL</span>
        <span className="text-right">Actions</span>
      </div>
      {rows.length > 0 && <MarginWarning className="mx-4 my-1.5" />}
      {rows.map((p) => (
        <PositionRow key={p.id} p={p} run={run} busy={busy} onEdit={() => setEditing(p.id)} />
      ))}
      {!rows.length && <Empty>No open positions</Empty>}
      {editor}
    </div>
  );
}

/** Names the master on a trade a dealer opened on the client's managed account; it has no Close or TP / SL. Wraps rather than truncates, so the whole name shows. */
function ManagedTag({ p }: { p: Position }) {
  if (!p.managedBy) return null;
  return (
    <span title="Opened by our dealing team on your managed account" className="inline-block max-w-full rounded-[10px] bg-managed-bg px-2 py-0.5 text-center font-sans text-[11px] leading-[15px] font-semibold break-words text-managed">
      {p.managedBy.name}
    </span>
  );
}

function ShortManagedTag({ name }: { name: string }) {
  return (
    <span title={`Managed by ${name}`} className="rounded-full bg-managed-bg px-1.5 py-px font-sans text-[10.5px] font-semibold text-managed">
      Managed
    </span>
  );
}

// ---- Pending orders ----
const ORD_COLS = 'grid grid-cols-[0.7fr_1fr_1.5fr_0.7fr_0.7fr_1fr_0.7fr_1fr_1fr_0.8fr_1.3fr_0.8fr] gap-2';

function OrderRow({ o, onCancel, busy }: { o: Order; onCancel: () => void; busy: boolean }) {
  const inst = useInstrument(o.symbol);
  const t = useTicker(o.symbol);
  const dp = inst?.priceDecimals ?? 2;
  const ccy = inst?.priceCcy ?? 'USD';
  const long = o.side === 'long';
  const cur = t ? D.from(long ? t.ask : t.bid) : null;
  const dist = cur && o.limitPrice && cur.isPos() ? D.from(o.limitPrice).sub(cur).div(cur).mul(100) : null;
  const lotUnit = inst && o.limitPrice ? D.from(o.limitPrice).mul(inst.lotSize).mul(usdPerPoint(inst.contractSize, t?.fx)) : null;
  const lots = o.lots ? fmt(o.lots, 2) : lotUnit && lotUnit.isPos() ? '≈' + fmt(D.from(o.notional).div(lotUnit), 4) : '—';
  return (
    <div className={cx(ORD_COLS, row)}>
      <TradeId id={o.tradeId} />
      <span className="text-muted">{time(o.createdAt)}</span>
      <AssetCell symbol={o.symbol} long={long} />
      <span className="font-sans">{orderTypeLabel(o.side, o.type)}</span>
      <span>{lots}</span>
      <span>{fmtUsd(o.notional)}</span>
      <span>{o.leverage}x</span>
      <span>{o.limitPrice ? fmtPrice(o.limitPrice, dp, ccy) : 'Market'}</span>
      <span>{cur ? fmtPrice(cur, dp, ccy) : '—'}</span>
      <span className="text-muted">{dist ? fmtPct(dist) : '—'}</span>
      <span>
        <span className="text-up">{o.tpPrice ? fmtPrice(o.tpPrice, dp, ccy) : '—'}</span>
        <span className="text-muted"> / </span>
        <span className="text-down">{o.slPrice ? fmtPrice(o.slPrice, dp, ccy) : '—'}</span>
      </span>
      <span className="text-right">
        <button type="button" disabled={busy} onClick={onCancel} className={btn}>
          Cancel
        </button>
      </span>
    </div>
  );
}

function OrderCard({ o, onCancel, busy }: { o: Order; onCancel: () => void; busy: boolean }) {
  const inst = useInstrument(o.symbol);
  const t = useTicker(o.symbol);
  const dp = inst?.priceDecimals ?? 2;
  const ccy = inst?.priceCcy ?? 'USD';
  const long = o.side === 'long';
  const cur = t ? D.from(long ? t.ask : t.bid) : null;
  return (
    <div className={card}>
      <div className="flex items-center justify-between gap-2">
        <CardAsset tradeId={o.tradeId} symbol={o.symbol} long={long} lev={o.leverage} />
        <span className="text-[11px] text-muted">
          {orderTypeLabel(o.side, o.type)} · <span className="num">{time(o.createdAt)}</span>
        </span>
      </div>
      <div className="num grid grid-cols-3 gap-2 text-xs">
        <Field label="Value">{fmtUsd(o.notional)}</Field>
        <Field label={o.type === 'stop' ? 'Stop Price' : 'Limit Price'}>{o.limitPrice ? fmtPrice(o.limitPrice, dp, ccy) : 'Market'}</Field>
        <Field label="Current">{cur ? fmtPrice(cur, dp, ccy) : '—'}</Field>
      </div>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="num truncate">
          <span className="font-sans text-muted">TP / SL </span>
          <span className="text-up">{o.tpPrice ? fmtPrice(o.tpPrice, dp, ccy) : '—'}</span>
          <span className="text-muted"> / </span>
          <span className="text-down">{o.slPrice ? fmtPrice(o.slPrice, dp, ccy) : '—'}</span>
        </span>
        <button type="button" disabled={busy} onClick={onCancel} className={cx(cardBtn, 'max-w-[120px]')}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function OrdersTable({ rows, run, busy, compact }: { rows: Order[]; run: Run; busy: string | null; compact: boolean }) {
  const cancel = (o: Order) => void run(o.id, () => api.del(`/api/orders/${o.id}`));
  if (compact) {
    return (
      <div>
        {rows.map((o) => (
          <OrderCard key={o.id} o={o} busy={busy === o.id} onCancel={() => cancel(o)} />
        ))}
        {!rows.length && <Empty>No pending orders</Empty>}
      </div>
    );
  }
  return (
    <div className="min-w-[1180px]">
      <div className={cx(ORD_COLS, head)}>
        <span>Order ID</span>
        <span>Created</span>
        <span>Asset</span>
        <span>Type</span>
        <span>Lots</span>
        <span>Value</span>
        <span>Leverage</span>
        <span>Order Price</span>
        <span>Current Price</span>
        <span>Distance</span>
        <span>TP / SL</span>
        <span className="text-right">Actions</span>
      </div>
      {rows.map((o) => (
        <OrderRow key={o.id} o={o} busy={busy === o.id} onCancel={() => cancel(o)} />
      ))}
      {!rows.length && <Empty>No pending orders</Empty>}
    </div>
  );
}

// ---- Closed positions ----
const CLS_COLS = 'grid grid-cols-[0.7fr_1fr_2.7fr_0.7fr_1fr_1fr_1fr_1fr_1fr_0.8fr_0.8fr_1fr] gap-2';

/** Why a closed position closed, as the client reads it. Closes by the client, the dealing desk or a copied lead trader all read "Closed". */
const CLOSE_REASON: Record<CloseReason, string> = {
  manual: 'Closed',
  tp: 'Take profit',
  sl: 'Stop loss',
  liquidation: 'Liquidation',
  admin: 'Closed',
  copy_mirror: 'Closed',
  copy_stop_loss: 'Copy stop loss',
  copy_stopped: 'Copy stopped',
  portfolio_rebalance: 'Portfolio rebalance',
  portfolio_stop_loss: 'Portfolio stop loss',
  portfolio_closed: 'Portfolio closed',
  delist_settlement: 'Delisted, settled',
};

function Reason({ p }: { p: Position }) {
  if (!p.closeReason) return <span className="text-muted">—</span>;
  return <span className={cx('font-sans', p.closeReason === 'liquidation' ? 'text-down' : 'text-muted')}>{CLOSE_REASON[p.closeReason]}</span>;
}
/** Hover text on Net PnL: how it is made up. */
function netBreakdown(p: Position) {
  const { realized } = closedCosts(p);
  return `Gross ${realized ? fmtUsdSigned(realized) : '—'} · Open fee ${fmtUsd(p.openFee)} · Close fee ${fmtUsd(p.closeFee ?? '0')} · Swap ${fmtUsdSigned(p.swap)}`;
}

function ClosedRow({ p }: { p: Position }) {
  const inst = useInstrument(p.symbol);
  const dp = inst?.priceDecimals ?? 2;
  const ccy = inst?.priceCcy ?? 'USD';
  const { fees, net } = closedCosts(p);
  const swap = D.from(p.swap);
  return (
    <div className={cx(CLS_COLS, row)}>
      <TradeId id={p.tradeId} />
      <span className="text-muted">{time(p.openedAt)}</span>
      <span className="flex min-w-0 items-center gap-2">
        <AssetCell symbol={p.symbol} long={p.side === 'long'} lev={p.leverage} />
        {p.managedBy && <ShortManagedTag name={p.managedBy.name} />}
      </span>
      <span title={`${fmt(p.qty, inst?.qtyDecimals ?? 4)} ${unitLabel(inst?.unit ?? '', p.qty)}`}>{fmtLots(p.qty, inst?.lotSize ?? '1')}</span>
      <span title="Position value at entry (margin × leverage)">{fmtUsd(D.from(p.margin).mul(p.leverage))}</span>
      <span>{fmtPrice(p.entryPrice, dp, ccy)}</span>
      <span>{p.closePrice ? fmtPrice(p.closePrice, dp, ccy) : '—'}</span>
      <span className="text-muted">{p.closedAt ? time(p.closedAt) : '—'}</span>
      <Reason p={p} />
      <span className="text-muted" title={`Open fee ${fmtUsd(p.openFee)} · Close fee ${fmtUsd(p.closeFee ?? '0')}`}>
        {fmtUsdSigned(fees.neg())}
      </span>
      <span className={swap.isZero() ? 'text-muted' : signCls(swap)}>{fmtUsdSigned(swap)}</span>
      <span className={cx('text-right font-semibold', signCls(net))} title={netBreakdown(p)}>
        {net ? fmtUsdSigned(net) : '—'}
      </span>
    </div>
  );
}

function ClosedCard({ p }: { p: Position }) {
  const inst = useInstrument(p.symbol);
  const dp = inst?.priceDecimals ?? 2;
  const ccy = inst?.priceCcy ?? 'USD';
  const { net } = closedCosts(p);
  return (
    <div className={card}>
      <div className="flex items-center justify-between gap-2">
        <CardAsset tradeId={p.tradeId} symbol={p.symbol} long={p.side === 'long'} lev={p.leverage} />
        <span className={cx('num font-semibold', signCls(net))}>{net ? fmtUsdSigned(net) : '—'}</span>
      </div>
      {p.managedBy && (
        <div>
          <ManagedTag p={p} />
        </div>
      )}
      <div className="num flex justify-between gap-3 text-[11.5px] text-muted">
        <span className="truncate">
          {fmtLots(p.qty, inst?.lotSize ?? '1')} lots · {fmtPrice(p.entryPrice, dp, ccy)} → {p.closePrice ? fmtPrice(p.closePrice, dp, ccy) : '—'}
        </span>
        <span className="whitespace-nowrap">
          {p.closedAt ? time(p.closedAt) : '—'}
          {p.closeReason && (
            <>
              {' '}
              · <Reason p={p} />
            </>
          )}
        </span>
      </div>
    </div>
  );
}

export function ClosedTable({ rows, compact }: { rows: Position[]; compact: boolean }) {
  if (compact) {
    return (
      <div>
        {rows.map((p) => (
          <ClosedCard key={p.id} p={p} />
        ))}
        {!rows.length && <Empty>No closed positions in this period</Empty>}
      </div>
    );
  }
  return (
    <div className="min-w-[1280px]">
      <div className={cx(CLS_COLS, head)}>
        <span>Order ID</span>
        <span>Opened</span>
        <span>Asset</span>
        <span>Lots</span>
        <span>Value</span>
        <span>Entry Price</span>
        <span>Exit Price</span>
        <span>Closed</span>
        <span>Reason</span>
        <span>Fees</span>
        <span>Swap</span>
        <span className="text-right">Net PnL</span>
      </div>
      {rows.map((p) => (
        <ClosedRow key={p.id} p={p} />
      ))}
      {!rows.length && <Empty>No closed positions in this period</Empty>}
    </div>
  );
}

// ---- Panel ----
type Tab = 'positions' | 'orders' | 'closed';
type Source = 'all' | 'own' | 'managed';

/** The client's own rows and their managed rows, newest first, filtered by source. */
function bySource(own: Position[], managed: Position[], source: Source): Position[] {
  if (source === 'own' || !managed.length) return own;
  if (source === 'managed') return managed;
  const key = (p: Position) => p.closedAt ?? p.openedAt;
  return [...own, ...managed].sort((a, b) => key(b).localeCompare(key(a)));
}

function SourceFilter({ value, onChange, counts }: { value: Source; onChange: (s: Source) => void; counts: Record<Source, number> | null }) {
  const opts: [Source, string][] = [
    ['all', 'All'],
    ['own', 'My trades'],
    ['managed', 'Managed'],
  ];
  return (
    <div role="radiogroup" aria-label="Show" className="flex shrink-0 gap-1.5 px-4 py-2">
      {opts.map(([id, label]) => (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={value === id}
          onClick={() => onChange(id)}
          className={cx('h-7 rounded-full px-3 text-[12px] whitespace-nowrap', value === id ? 'bg-fg font-semibold text-bg' : 'bg-raised font-medium text-muted hover:text-fg')}
        >
          {label}
          {counts && <span className="num ml-1 opacity-70">{counts[id]}</span>}
        </button>
      ))}
    </div>
  );
}
function Count({ n, on }: { n: number; on: boolean }) {
  return <span className={cx('num inline-grid h-[18px] min-w-[18px] place-items-center rounded-full px-1.5 text-[11px]', on ? 'bg-accent-bg text-accent-fg' : 'bg-raised text-fg')}>{n}</span>;
}

/** Live unrealized PnL and margin for a set of open positions. */
export function useOpenTotals(rows: Position[]) {
  const tickers = useStore(marketStore, (s) => s.tickers);
  const instruments = useStore(marketStore, (s) => s.bySymbol);
  return useMemo(() => {
    let u = D.ZERO;
    let m = D.ZERO;
    for (const p of rows) {
      m = m.add(p.marginHeld ?? p.margin);
      const t = tickers[p.symbol];
      if (!t) continue;
      const exit = closePrice(p.side, { bid: D.from(t.bid), ask: D.from(t.ask) });
      u = u.add(pnl(p.side, p.entryPrice, exit, p.qty, usdPerPoint(instruments[p.symbol]?.contractSize ?? '1', t.fx))).add(p.swap);
    }
    return { upnl: u, margin: m };
  }, [rows, tickers, instruments]);
}

/**
 * Account-wide balance, equity and margin, marked live from the tickers.
 * Free margin = equity − used margin − pending order holds; margin level = equity ÷ used margin.
 * With a managed account or Smart Portfolio investments, Balance, Equity and Open P/L also count that
 * money; the margin figures stay the trading account's own (it can't back the client's own trades).
 */
function AccountBar({ positions, compact, actions }: { positions: Position[]; compact: boolean; actions?: ReactNode }) {
  const account = useActiveAccount();
  const { upnl: ownUpnl } = useOpenTotals(positions);
  const managed = useManagedTotals();
  const managedAccounts = useManagedAccounts();
  const smart = useInvestmentTotals();
  const smartCount = useInvestmentAccounts().length;
  const [allocating, setAllocating] = useState(false);
  if (!account) return actions ? <ActionsBar>{actions}</ActionsBar> : null;
  const credit = D.from(account.credit);
  const ownEquity = D.from(account.balance).add(credit).add(ownUpnl);
  const used = D.from(account.marginUsed);
  const free = freeMargin(ownEquity, used, account.orderHold);
  const level = used.isPos() ? `${fmt(ownEquity.div(used).mul(100), 2)}%` : '—';
  const subs = [managed, smart].filter((t) => t !== null);
  const balance = subs.reduce((a, t) => a.add(t.balance), D.from(account.balance));
  const equity = subs.reduce((a, t) => a.add(t.equity), ownEquity);
  const upnl = subs.reduce((a, t) => a.add(t.upnl), ownUpnl);
  const upnlCls = positions.length || subs.length ? signCls(upnl) : 'text-muted';
  const names = managedAccounts.map((m) => m.copy.managedBy?.name ?? m.copy.leader.displayName).join(', ');
  const smartName = smartCount === 1 ? 'Smart Portfolio' : 'Smart Portfolios';

  const balanceLabel = subs.length ? (
    <span className="inline-flex items-center gap-1">
      Total balance
      <HelpTip
        help={{
          title: 'Total balance',
          text: `Trading account ${fmtUsd(account.balance)}${managed ? ` + managed by ${names} ${fmtUsd(managed.balance)}` : ''}${smart ? ` + ${smartName} ${fmtUsd(smart.balance)}` : ''}.`,
        }}
      />
    </span>
  ) : (
    'Balance'
  );
  const freeLabel = subs.length ? (
    <span className="inline-flex items-center gap-1">
      Free Margin
      <HelpTip
        help={{
          title: 'Free margin',
          text: `For your own trades, on your trading account. ${managed && smart ? 'Managed money and Smart Portfolios are' : managed ? 'Managed money is' : 'Smart Portfolios are'} not part of it.`,
        }}
      />
    </span>
  ) : (
    'Free Margin'
  );
  const allocate = managed && (
    <button type="button" onClick={() => setAllocating(true)} className="h-8 shrink-0 rounded-full border border-managed/40 bg-managed-bg px-3.5 text-[13px] font-semibold whitespace-nowrap text-managed">
      Allocate funds
    </button>
  );
  const dialog = allocating && <AllocateFundsDialog accounts={managedAccounts} onClose={() => setAllocating(false)} />;
  const managedValue = managed && (
    <>
      <span className="text-managed">{fmtUsd(managed.equity)}</span> <span className={cx('text-[12px]', signCls(managed.pnl))}>{fmtUsdSigned(managed.pnl)}</span>
    </>
  );
  const smartValue = smart && (
    <>
      {fmtUsd(smart.equity)} <span className={cx('text-[12px]', signCls(smart.pnl))}>{fmtUsdSigned(smart.pnl)}</span>
    </>
  );

  if (compact) {
    return (
      <div className="mx-4 mt-3 mb-1 shrink-0 rounded-xl border border-line bg-panel p-3.5">
        <div className="flex items-end justify-between gap-3">
          <Field label="Equity">
            <span className="num mt-0.5 text-[22px] font-bold">{fmtUsd(equity)}</span>
          </Field>
          <Field label="Open P/L" className="items-end">
            <span className={cx('num mt-0.5 text-base font-semibold', upnlCls)}>{fmtUsdSigned(upnl)}</span>
          </Field>
        </div>
        <div className="num mt-3.5 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-line pt-3 text-sm font-semibold">
          <Field label={balanceLabel}>{fmtUsd(balance)}</Field>
          <Field label={freeLabel}>
            <span className="text-accent-fg">{fmtUsd(free)}</span>
          </Field>
          <Field label="Used Margin">{fmtUsd(used)}</Field>
          <Field label="Margin Level">{level}</Field>
        </div>
        {managed && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-managed-bg px-3 py-2.5 text-[13px]">
            <span className="min-w-0 truncate">
              <span className="font-semibold text-managed">Managed</span> <span className="text-muted">· {names}</span>
            </span>
            <span className="num shrink-0 font-semibold">{managedValue}</span>
          </div>
        )}
        {managed && <div className="mt-3 flex [&>button]:h-10 [&>button]:flex-1">{allocate}</div>}
        {smart && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-raised px-3 py-2.5 text-[13px]">
            <span className="font-semibold">{smartName}</span>
            <span className="num shrink-0 font-semibold">{smartValue}</span>
          </div>
        )}
        {dialog}
      </div>
    );
  }

  const items: [ReactNode, ReactNode, string?][] = [
    [balanceLabel, fmtUsd(balance)],
    ...(credit.isPos() ? ([['Credit', fmtUsd(credit)]] as [string, string][]) : []),
    ['Equity', fmtUsd(equity)],
    ...(managedValue
      ? ([
          [
            'Managed',
            <span className="flex items-center gap-2.5">
              <span>{managedValue}</span>
              <button
                type="button"
                onClick={() => setAllocating(true)}
                className="h-6 rounded-full border border-managed/40 bg-managed-bg px-2.5 font-sans text-[11.5px] font-semibold text-managed hover:border-managed"
              >
                Allocate funds
              </button>
            </span>,
          ],
        ] as [string, ReactNode][])
      : []),
    ...(smartValue ? ([[smartName, smartValue]] as [string, ReactNode][]) : []),
    ['Used Margin', fmtUsd(used)],
    [freeLabel, fmtUsd(free), 'text-accent-fg'],
    ['Margin Level', level],
    ['Open P/L', fmtUsdSigned(upnl), upnlCls],
  ];
  return (
    <dl className="m-0 flex shrink-0 items-center overflow-x-auto border-b border-line bg-panel py-2.5">
      {items.map(([k, v, cls], i) => (
        <div key={i} className={cx('flex flex-col px-5 whitespace-nowrap', i === 0 ? 'pl-4' : 'border-l border-line')}>
          <dt className="text-[11px] text-muted">{k}</dt>
          <dd className={cx('num m-0 mt-0.5 text-[15px] font-semibold', cls ?? 'text-fg')}>{v}</dd>
        </div>
      ))}
      {actions && <div className="ml-auto flex shrink-0 items-center gap-1.5 pr-4 pl-5">{actions}</div>}
      {dialog}
    </dl>
  );
}

/** The account bar's right-hand actions on their own, when there are no figures to show beside them. */
function ActionsBar({ children }: { children: ReactNode }) {
  return <div className="flex shrink-0 items-center justify-end gap-1.5 border-b border-line bg-panel px-4 py-2.5">{children}</div>;
}

/** Totals for the closed positions in the chosen time frame; Closed P/L sits bottom right. */
function ClosedFooter({ rows, period, compact }: { rows: Position[]; period: Period; compact: boolean }) {
  const { count, net, fees, swap, winRate } = closedTotals(rows);
  return (
    <div className={cx('flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-t border-line bg-panel px-4 py-2 text-xs text-muted', compact && 'sticky bottom-0 z-[5]')}>
      <span>
        Trades <span className="num font-semibold text-fg">{count}</span>
      </span>
      <span>
        Win rate <span className="num font-semibold text-fg">{winRate}</span>
      </span>
      <span>
        Fees <span className="num font-semibold text-fg">{fmtUsdSigned(fees.neg())}</span>
      </span>
      <span>
        Swap <span className="num font-semibold text-fg">{fmtUsdSigned(swap)}</span>
      </span>
      <span className="ml-auto flex items-baseline gap-2">
        Closed P/L · {periodLabel(period)}
        <span className={cx('num text-[15px] font-semibold', signCls(count ? net : null))}>{fmtUsdSigned(net)}</span>
      </span>
    </div>
  );
}

function ConfirmDialog({ title, body, rows, action, busy, onConfirm, onClose }: { title: string; body: string; rows: [string, ReactNode][]; action: string; busy: boolean; onConfirm: () => void; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={title} width={380}>
      <p className="m-0 text-[13px] text-muted">{body}</p>
      <dl className="num m-0 flex flex-col gap-1.5 rounded-lg bg-raised px-3 py-2.5 text-[13px]">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3">
            <dt className="font-sans text-muted">{k}</dt>
            <dd className="m-0 font-semibold">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="grid grid-cols-2 gap-2.5">
        <button type="button" onClick={onClose} className="h-[42px] rounded-lg border border-line-strong font-semibold text-fg">
          Cancel
        </button>
        <button type="button" disabled={busy} onClick={onConfirm} className="h-[42px] rounded-lg bg-btn-short font-semibold text-btn-short-fg disabled:opacity-50">
          {busy ? <Spinner /> : action}
        </button>
      </div>
    </Modal>
  );
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** Asks before closing every open position: shows the count, margin released and P/L that will be locked in. */
export function CloseAllDialog({ positions, busy, onConfirm, onClose }: { positions: Position[]; busy: boolean; onConfirm: () => void; onClose: () => void }) {
  const { upnl, margin } = useOpenTotals(positions);
  return (
    <ConfirmDialog
      title="Close all positions?"
      body={`${plural(positions.length, 'open position')} will close at market price. This cannot be undone.`}
      rows={[
        ['Positions', positions.length],
        ['Margin released', fmtUsd(margin)],
        ['Estimated P/L', <span className={signCls(upnl)}>{fmtUsdSigned(upnl)}</span>],
      ]}
      action="Close all"
      busy={busy}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/** Asks before cancelling every pending order. */
export function CancelAllDialog({ orders, busy, onConfirm, onClose }: { orders: Order[]; busy: boolean; onConfirm: () => void; onClose: () => void }) {
  const value = orders.reduce((a, o) => a.add(o.notional), D.ZERO);
  return (
    <ConfirmDialog
      title="Cancel all orders?"
      body={`${plural(orders.length, 'pending order')} will be cancelled. This cannot be undone.`}
      rows={[
        ['Orders', orders.length],
        ['Total value', fmtUsd(value)],
      ]}
      action="Cancel all"
      busy={busy}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/**
 * `actions` sit at the right end of the desktop account bar (the trade page's Instant trade and chart settings).
 * `collapsed` (desktop trade page) hides the table under the tab strip; a chevron on the strip, or any tab,
 * reports the change through `onCollapse`.
 */
export function BottomTabs({ summary = true, actions, collapsed = false, onCollapse }: { summary?: boolean; actions?: ReactNode; collapsed?: boolean; onCollapse?: (collapsed: boolean) => void }) {
  const compact = !useMedia(DESKTOP_QUERY);
  const [tab, setTab] = useState<Tab>('positions');
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [confirm, setConfirm] = useState<'close' | 'cancel' | null>(null);
  const [source, setSource] = useState<Source>('all');
  const positions = usePositions();
  const orders = useOrders();
  const closed = useClosedPositions();
  const managedOpen = useManagedPositions();
  const managedClosed = useManagedClosed();
  const me = useMe();
  const account = useActiveAccount();
  const { err, busy, run } = useAction();

  // Managed trades (dealer-run, on the client's managed account) are listed with the client's own.
  const hasManaged = managedOpen.length > 0 || managedClosed.length > 0;
  const openRows = bySource(positions, managedOpen, source);
  const closedRows = inPeriod(bySource(closed, managedClosed, source), period);

  const tabs: { id: Tab; label: string; short: string; count?: number }[] = [
    { id: 'positions', label: 'Open Positions', short: 'Open', count: positions.length + managedOpen.length },
    { id: 'orders', label: 'Pending Orders', short: 'Pending', count: orders.length },
    { id: 'closed', label: 'Closed Positions', short: 'Closed' },
  ];

  const closeAll = () => account && run('all', () => api.post('/api/positions/close-all', { accountId: account.id })).then(() => setConfirm(null));
  const cancelAll = () => account && run('all', () => api.del(`/api/orders?accountId=${account.id}`)).then(() => setConfirm(null));

  const tools = me && account && (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5 text-xs text-muted">
      {compact && <span className="flex-1" />}
      {tab === 'positions' && source !== 'managed' && positions.length > 0 && (
        <button type="button" disabled={busy === 'all'} onClick={() => setConfirm('close')} className="min-h-7 font-semibold text-down disabled:opacity-50">
          Close All
        </button>
      )}
      {tab === 'orders' && orders.length > 0 && (
        <button type="button" disabled={busy === 'all'} onClick={() => setConfirm('cancel')} className="min-h-7 font-semibold text-down disabled:opacity-50">
          Cancel All
        </button>
      )}
      {tab === 'closed' && <PeriodSelect value={period} onChange={setPeriod} />}
      <button
        type="button"
        onClick={() => {
          void reloadTrading(account.id);
          void reloadManaged();
        }}
        className="min-h-7 hover:text-fg"
      >
        Refresh
      </button>
    </div>
  );
  return (
    // Mobile: the whole panel scrolls inside the tab, with the tabs and the closed totals pinned.
    <div className={cx('flex flex-col', compact ? 'min-h-full' : 'h-full')}>
      {me && summary ? (
        <AccountBar positions={positions} compact={compact} actions={compact ? undefined : actions} />
      ) : (
        !compact && actions && <ActionsBar>{actions}</ActionsBar>
      )}
      {compact ? (
        <div className="sticky top-0 z-10 shrink-0 bg-bg">
          <div role="tablist" className="flex gap-2 overflow-x-auto border-b border-line px-4 py-2.5">
            {tabs.map((t) => (
              <button
                key={t.id}
                role="tab"
                type="button"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={cx('flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12.5px] whitespace-nowrap', tab === t.id ? 'border-line-strong bg-raised font-semibold text-fg' : 'border-line font-medium text-muted')}
              >
                {t.short}
                {t.count !== undefined && t.count > 0 && <Count n={t.count} on />}
              </button>
            ))}
          </div>
          {tools && <div className="border-b border-line px-4">{tools}</div>}
        </div>
      ) : (
        <div className="flex shrink-0 flex-wrap items-center gap-x-4 border-b border-line px-4">
          <div role="tablist" className="flex gap-5 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.id}
                role="tab"
                type="button"
                aria-selected={tab === t.id}
                onClick={() => {
                  setTab(t.id);
                  if (collapsed) onCollapse?.(false);
                }}
                className={cx('flex items-center gap-1.5 border-b-2 pt-3 pb-2.5 text-[13px] whitespace-nowrap', tab === t.id ? 'border-fg font-semibold text-fg' : 'border-transparent font-medium text-muted hover:text-fg')}
              >
                {t.label}
                {t.count !== undefined && t.count > 0 && <Count n={t.count} on={tab === t.id} />}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          {tools}
          {onCollapse && (
            <button
              type="button"
              onClick={() => onCollapse(!collapsed)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand the panel' : 'Collapse the panel'}
              title={collapsed ? 'Expand' : 'Collapse'}
              className="grid size-6 place-items-center rounded-md text-muted hover:bg-raised hover:text-fg"
            >
              <Icon name="chevron" size={14} className={cx('transition-transform', collapsed && 'rotate-180')} />
            </button>
          )}
        </div>
      )}
      {!collapsed && (
        <>
          {err && <div className="px-4 py-2 text-xs text-down">{err}</div>}
          {me && hasManaged && tab !== 'orders' && (
            <SourceFilter
              value={source}
              onChange={setSource}
              counts={tab === 'positions' ? { all: positions.length + managedOpen.length, own: positions.length, managed: managedOpen.length } : null}
            />
          )}
          <div className={cx('flex-1', !compact && 'overflow-auto')}>
            {!me ? (
              <Empty>Log in to see your positions and orders</Empty>
            ) : tab === 'positions' ? (
              <PositionsTable rows={openRows} run={run} busy={busy} compact={compact} />
            ) : tab === 'orders' ? (
              <OrdersTable rows={orders} run={run} busy={busy} compact={compact} />
            ) : (
              <ClosedTable rows={closedRows} compact={compact} />
            )}
          </div>
          {me && tab === 'closed' && <ClosedFooter rows={closedRows} period={period} compact={compact} />}
        </>
      )}
      {confirm === 'close' && positions.length > 0 && <CloseAllDialog positions={positions} busy={busy === 'all'} onConfirm={() => void closeAll()} onClose={() => setConfirm(null)} />}
      {confirm === 'cancel' && orders.length > 0 && <CancelAllDialog orders={orders} busy={busy === 'all'} onConfirm={() => void cancelAll()} onClose={() => setConfirm(null)} />}
    </div>
  );
}
